import React, { useEffect, useState } from 'react';
import {
  formatUkDateInput,
  parseUkDateInput,
  type PromotionDetail as PromotionDetailData,
} from './promotionTypes';

const LANGUAGES = ['en', 'fr', 'de', 'es', 'it'] as const;
const WEEKDAYS = [
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
  { label: 'Sunday', value: 0 },
];
const PARTS_OF_DAY = ['morning', 'afternoon', 'evening'] as const;

interface WhatsNewPromotionPanelProps {
  detail: PromotionDetailData;
  busy: string | null;
  onRefresh: () => void;
  onStart: () => void;
  onSave: (settings: {
    startDate: string;
    endDate: string;
    weekdays: number[];
    partsOfDay: Array<'morning' | 'afternoon' | 'evening'>;
  }) => void;
}

const WhatsNewPromotionPanel: React.FC<WhatsNewPromotionPanelProps> = ({
  detail,
  busy,
  onRefresh,
  onStart,
  onSave,
}) => {
  const { definition } = detail.promotion;
  const editable = detail.promotion.status === 'draft';
  const [startDate, setStartDate] = useState(
    formatUkDateInput(definition.startDate),
  );
  const [endDate, setEndDate] = useState(formatUkDateInput(definition.endDate));
  const [weekdays, setWeekdays] = useState(definition.slot.weekdays);
  const [partsOfDay, setPartsOfDay] = useState(definition.slot.partsOfDay);
  const startIso = parseUkDateInput(startDate);
  const endIso = parseUkDateInput(endDate);
  const endDateBeforeStart = Boolean(startIso && endIso && endIso < startIso);
  const settingsValid =
    Boolean(startIso && endIso) &&
    !endDateBeforeStart &&
    weekdays.length > 0 &&
    partsOfDay.length > 0;

  useEffect(() => {
    setStartDate(formatUkDateInput(definition.startDate));
    setEndDate(formatUkDateInput(definition.endDate));
    setWeekdays(definition.slot.weekdays);
    setPartsOfDay(definition.slot.partsOfDay);
  }, [definition]);

  const toggleWeekday = (value: number) => {
    setWeekdays((current) =>
      current.includes(value)
        ? current.filter((day) => day !== value)
        : [...current, value],
    );
  };
  const togglePartOfDay = (value: (typeof PARTS_OF_DAY)[number]) => {
    setPartsOfDay((current) =>
      current.includes(value)
        ? current.filter((part) => part !== value)
        : [...current, value],
    );
  };

  const templates = detail.cardTemplates ?? [];

  return (
    <section className='launch-card'>
      <div className='launch-card-header'>
        <h4>{detail.promotion.name} templates</h4>
        <button
          type='button'
          className='launch-btn launch-btn--quiet'
          disabled={busy !== null}
          onClick={onRefresh}
        >
          {busy === 'refresh' ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <dl>
        {LANGUAGES.map((language) => {
          const template = templates.find((item) => item.language === language);
          return (
            <div key={language}>
              <dt>{language.toUpperCase()}</dt>
              <dd>
                {template ? template.status.replace('_', ' ') : 'Not submitted'}
              </dd>
              {template?.rejectionReason && <dd>{template.rejectionReason}</dd>}
            </div>
          );
        })}
      </dl>
      {detail.cohortSize !== null && detail.cohortSize !== undefined && (
        <p>Goes to {detail.cohortSize} people</p>
      )}
      {editable && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!settingsValid || !startIso || !endIso) return;
            onSave({
              startDate: startIso,
              endDate: endIso,
              weekdays,
              partsOfDay: [...partsOfDay],
            });
          }}
        >
          <h4>Schedule settings</h4>
          <label>
            Start date
            <input
              value={startDate}
              placeholder='dd/mm/yyyy'
              inputMode='numeric'
              disabled={!editable || busy !== null}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </label>
          <label>
            End date
            <input
              value={endDate}
              placeholder='dd/mm/yyyy'
              inputMode='numeric'
              disabled={!editable || busy !== null}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </label>
          {endDateBeforeStart && (
            <p className='promotion-validation-note' role='alert'>
              End date must be on or after the start date.
            </p>
          )}
          <fieldset disabled={!editable || busy !== null}>
            <legend>Weekdays</legend>
            {WEEKDAYS.map(({ label, value }) => (
              <label key={value}>
                <input
                  type='checkbox'
                  checked={weekdays.includes(value)}
                  onChange={() => toggleWeekday(value)}
                />
                {label}
              </label>
            ))}
          </fieldset>
          <fieldset disabled={!editable || busy !== null}>
            <legend>Time of day</legend>
            {PARTS_OF_DAY.map((part) => (
              <label key={part}>
                <input
                  type='checkbox'
                  checked={partsOfDay.includes(part)}
                  onChange={() => togglePartOfDay(part)}
                />
                {part}
              </label>
            ))}
          </fieldset>
          <button
            type='submit'
            className='launch-btn'
            disabled={!settingsValid || busy !== null}
          >
            {busy === 'settings' ? 'Saving…' : 'Save settings'}
          </button>
          {detail.promotion.status === 'draft' && (
            <button
              type='button'
              className='launch-btn launch-btn--primary'
              disabled={busy !== null}
              onClick={onStart}
            >
              {busy === 'launch' ? 'Starting…' : 'Start'}
            </button>
          )}
        </form>
      )}
    </section>
  );
};

export default WhatsNewPromotionPanel;
